import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { motion } from 'framer-motion';
import { ArrowLeftIcon, PanelRightClose, PanelRightOpen, File, Paperclip } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FixedSizeList as List } from 'react-window';
import { toast } from 'sonner';

import FactAdderModal from '../../Modals/FactAdderModal';
import DocViewerFactSidebar from '../components/DocViewerFactSidebar';
import DocViewerFullFact from '../components/DocViewerFullFact';
import { processFactHighlights, sortFactsByAppearance } from '../components/utils';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetDoc } from '@/api/queries/useGetDoc';
import { useGetDocFacts } from '@/api/queries/useGetDocFacts';
import Button from '@/components/atoms/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/atoms/Tooltip';
import ChildDocumentsPopover from '@/components/molecules/ChildDocumentsPopover';
import ToastDrawer from '@/components/molecules/ToastDrawer';
import PDFViewer, { PDFViewerRef } from '@/components/organisms/PDFViewer';
import { useDocumentNavigation } from '@/hooks/useOpenDocViewer';
import { Fact } from '@/types';

const DocViewer = ({ caseId, docId }: { caseId: string; docId: string }) => {
  // State
  const listRef = useRef<List>(null);
  const pdfViewerRef = useRef<PDFViewerRef>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Get initial searchQuery from URL params
  const searchParams = new URLSearchParams(location.search);
  const initialSearchQuery = searchParams.get('searchQuery');

  const [highlightedFactId, setHighlightedFactId] = useState<string | null>(null);
  const [selectedFact, setSelectedFact] = useState<Fact | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [factAdderModalIsOpen, setFactAdderModalIsOpen] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  const { closeDocViewer } = useDocumentNavigation();

  // Data fetching -------------
  const { data: responseFacts, isLoading } = useGetDocFacts({ docId, caseId });
  const { data: doc } = useGetDoc(caseId, docId);
  const docName = doc?.file?.name;

  // Coordinate and fact processing
  const { sortedFacts, coordinates, highlightFactMap } = useMemo(() => {
    if (!responseFacts) return { sortedFacts: [], coordinates: [] };
    const sortedFacts = sortFactsByAppearance(responseFacts, docId);
    const { coordinates, highlightFactMap } = processFactHighlights(sortedFacts, docId);

    return { sortedFacts, coordinates, highlightFactMap };
  }, [responseFacts, docId]);

  const handleNewFactCreated = useCallback(() => {
    setFactAdderModalIsOpen(false);
    trackCustomEvent(TrackingEvents.CreateFactFromDocViewer, { caseId, docId });
  }, [caseId, docId]);

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
    setSelectedFact(null);
  };

  const selectFact = (fact: Fact) => {
    // Clear search when selecting a fact
    pdfViewerRef.current?.clearSearch();
    setSelectedFact(fact);
    setIsDrawerOpen(true);
  };

  const scrollToFact = (id: string) => {
    // Clear search when clicking on a fact highlight
    pdfViewerRef.current?.clearSearch();

    const index = highlightFactMap?.get(id);
    if (index !== undefined && listRef.current) {
      setHighlightedFactId(id);
      listRef.current.scrollToItem(index, 'center');

      // Find the fact in the sortedFacts array
      const fact = sortedFacts.find((fact) => fact.id === id);
      if (fact) {
        const location = fact.locations.find((location) => location.docId === docId);
        if (location) {
          navigator.clipboard.writeText(location?.sources?.map((source) => source.text).join('\n') ?? '');
          toast.success('Source text copied to clipboard');
        }
      }
    }
  };

  const scrollToHighlight = (id: string) => {
    // Clear search when clicking magnifying glass
    pdfViewerRef.current?.clearSearch();
    setHighlightedFactId(id);
  };

  // Check for factId and searchQuery in URL params, then remove them after use
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const factIdFromUrl = searchParams.get('factId');
    const hasSearchQuery = searchParams.has('searchQuery');
    let shouldUpdateUrl = false;

    if (factIdFromUrl && highlightFactMap && highlightFactMap.has(factIdFromUrl)) {
      // Scroll to the fact
      scrollToHighlight(factIdFromUrl);
      searchParams.delete('factId');
      shouldUpdateUrl = true;
    }

    // Remove searchQuery from URL after initial load
    if (hasSearchQuery) {
      searchParams.delete('searchQuery');
      shouldUpdateUrl = true;
    }

    if (shouldUpdateUrl) {
      const newUrl = `${location.pathname}?${searchParams.toString()}`;
      navigate(newUrl, { replace: true });
    }
  }, [highlightFactMap, location, navigate]);

  return (
    <div className="flex h-full">
      <div className={`overflow-hidden transition-all duration-300 ${isSidebarVisible ? 'w-7/12' : 'w-full'}`}>
        <div className={`flex h-14 items-center justify-between border-b ${isSidebarVisible ? 'pr-2' : ''}`}>
          <div className="grid w-full grid-cols-[min-content_1fr] items-center gap-4 pr-2">
            <Button
              className="flex items-center rounded-full border bg-gray-100 px-2 py-0.5 text-sm whitespace-nowrap hover:bg-gray-200"
              onClick={closeDocViewer}
              text="All Docs"
              icon={<ArrowLeftIcon className="h-5 w-5 pr-1" />}
            />

            <div className="flex w-full flex-col">
              <p className="line-clamp-1 max-w-11/12 items-center break-all text-ellipsis">{docName}</p>

              {doc?.file?.parentFile && (
                <div className="flex items-center gap-1 text-xs">
                  <File size={12} className="text-blue-600" />
                  <span>Attachment of:</span>
                  <Link
                    className="text-blue-600 underline hover:text-blue-700"
                    to={(() => {
                      if (doc?.file?.parentFile?.doc?.id) {
                        const searchParams = new URLSearchParams(location.search);
                        searchParams.set('docId', doc.file.parentFile.doc.id);
                        return location.pathname + '?' + searchParams.toString();
                      }

                      return location.pathname;
                    })()}
                  >
                    {doc.file.parentFile.name}
                  </Link>
                </div>
              )}

              {doc?.file?.childFiles && doc.file.childFiles.length > 0 && (
                <ChildDocumentsPopover childFiles={doc?.file?.childFiles}>
                  <button className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700">
                    <Paperclip size={12} />
                    <span>
                      {doc.file.childFiles.length} attachment{doc.file.childFiles.length !== 1 ? 's' : ''}
                    </span>
                  </button>
                </ChildDocumentsPopover>
              )}
            </div>
          </div>

          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`flex h-8 w-8 items-center justify-center rounded-full border bg-white text-gray-600 shadow-sm transition-all duration-200 hover:bg-gray-50 hover:text-gray-800 hover:shadow-md`}
                  onClick={() => setIsSidebarVisible(!isSidebarVisible)}
                  aria-label={isSidebarVisible ? 'Hide facts sidebar' : 'Show facts sidebar'}
                >
                  {isSidebarVisible ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isSidebarVisible ? 'Hide facts' : 'Show facts'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="relative h-[calc(100%-4rem)]">
          <PDFViewer
            ref={pdfViewerRef}
            highlightedFactId={highlightedFactId}
            setHighlightedFactId={setHighlightedFactId}
            highlights={coordinates}
            onHighlightClick={scrollToFact}
            docId={docId}
            caseId={caseId}
            hideFeatureButtons={true}
            fileName={docName}
            initialSearchQuery={initialSearchQuery ?? undefined}
          />
        </div>
      </div>

      {isSidebarVisible && (
        <DocViewerFactSidebar
          docId={docId}
          isLoading={isLoading}
          factTotal={responseFacts?.length ?? null}
          jumpToHighlightArea={scrollToHighlight}
          highlightedFactId={highlightedFactId}
          selectFact={selectFact}
          facts={sortedFacts}
          listRef={listRef}
          setFactAdderModalIsOpen={setFactAdderModalIsOpen}
        />
      )}

      <ToastDrawer isOpen={isDrawerOpen} toggleDrawer={toggleDrawer} position="right">
        {selectedFact && (
          <motion.div key={selectedFact.id}>
            <DocViewerFullFact fact={selectedFact} docId={docId} showDocumentDetails />
          </motion.div>
        )}
      </ToastDrawer>

      <FactAdderModal
        caseId={caseId}
        docId={docId}
        onSuccess={handleNewFactCreated}
        onClose={() => setFactAdderModalIsOpen(false)}
        isOpen={factAdderModalIsOpen}
      />
    </div>
  );
};

export default DocViewer;
